/**
 * ProductControl - ops change which product a school has: School Operations
 * & Finance, the CBC Academic & Learning Suite, or All-in-one (education
 * strategy), on the school's behalf.
 *
 * The same rules as the school's own change on its billing page: free before
 * billing starts; once billed, a bigger product now with the rest of the
 * period invoiced, a smaller one from the next period. The reason is kept in
 * the audit log. A vertical that sells one thing shows nothing.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { adminService, type ProductChangeResult, type TenantDetail, type TenantProducts } from '../services/admin'
import { LoadingButton } from './LoadingButton'
import { apiErrorMessage } from '@lipaflow/shared'

const MIN_REASON = 10

interface Props {
  tenant: TenantDetail
  onChanged: () => void
}

const money = (amount: number, currency = 'KES') => `${currency} ${Math.round(amount).toLocaleString('en-KE')}`
const day = (iso: string) => new Date(iso).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })

function outcome(result: ProductChangeResult, name: string): string {
  if (result.when === 'next_period' && result.effective_from) return `${name} from ${day(result.effective_from)}`
  if (result.when === 'now_charged') return `${name} now; invoice ${result.invoice_number ?? ''} for ${money(result.amount)}`
  if (result.when === 'unchanged') return `Staying on ${name}`
  return `${name} now`
}

export function ProductControl({ tenant, onChanged }: Props) {
  const queryClient = useQueryClient()
  const { data } = useQuery({
    queryKey: ['tenant-product', tenant.id],
    queryFn: () => adminService.getProduct(tenant.id),
  })
  const [product, setProduct] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  const save = useMutation({
    mutationFn: () => adminService.setProduct(tenant.id, product as string, reason.trim()),
    onSuccess: (result) => {
      const name = data?.products.find((p) => p.code === result.product_code)?.name ?? result.product_code
      toast.success(outcome(result, name))
      setReason('')
      setProduct(null)
      queryClient.invalidateQueries({ queryKey: ['tenant-product', tenant.id] })
      onChanged()
    },
    onError: (err: unknown) => {
      toast.error(apiErrorMessage(err, 'Failed to update the product'))
    },
  })

  if (!data || data.products.length === 0) return null

  const current = data.products.find((p) => p.code === data.product)
  const pending = data.products.find((p) => p.code === data.pending_product)
  const selected = product ?? data.product ?? ''
  const changed = selected !== '' && selected !== data.product
  const canSave = changed && reason.trim().length >= MIN_REASON
  const priced = (p: TenantProducts['products'][number]) =>
    data.count > 0 && p.per_period !== null ? ` · ${money(p.per_period, p.currency)} a ${data.period}` : ''

  return (
    <section aria-labelledby="product-heading" className="space-y-3 rounded-xl border border-stone-200 p-4">
      <div>
        <h4 id="product-heading" className="font-inter text-body font-medium text-stone-800">
          Product
        </h4>
        <p className="font-inter text-body-sm text-stone-500">
          Currently: {current?.name ?? 'Not chosen yet (setup)'}
          {pending && `; moving to ${pending.name} next ${data.period}`}
          {data.count > 0 && ` · ${data.count.toLocaleString('en-KE')} ${data.unit_label}`}
        </p>
      </div>

      <div>
        <label htmlFor="tenant-product" className="mb-1 block text-sm font-medium text-stone-700">
          Change to
        </label>
        <select
          id="tenant-product"
          value={selected}
          onChange={(e) => setProduct(e.target.value)}
          className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
        >
          {!data.product && <option value="">Not chosen</option>}
          {data.products.map((option) => (
            <option key={option.code} value={option.code}>
              {option.name}
              {priced(option)}
            </option>
          ))}
        </select>
      </div>

      {changed && (
        <div>
          <p role="note" className="mb-3 rounded-md bg-warning-50 px-3 py-2 text-sm text-warning-800">
            {data.billing_started
              ? `Billed customer: a bigger product starts now and the rest of this ${data.period} is invoiced; a smaller one starts next ${data.period}.`
              : 'Not billed yet: the change is free and takes effect now.'}
          </p>
          <label htmlFor="product-reason" className="mb-1 block text-sm font-medium text-stone-700">
            Reason (kept in the audit log)
          </label>
          <textarea
            id="product-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="e.g. Bursar wants fees and attendance only; academics on another system"
            className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
          <div className="mt-2 flex justify-end">
            <LoadingButton
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={!canSave}
              loadingText="Saving..."
              size="sm"
            >
              Save product
            </LoadingButton>
          </div>
        </div>
      )}
    </section>
  )
}
